import { useMemo, useEffect } from "react";
import {
  DefaultColumnFilter,
  SelectColumnFilter,
  getCommonFilterTypes,
  StatusColumnFilter,
  CodStatusColumnFilter,
  alphaNumericCaseInsensitive,
} from "../../../helpers/commonTableFunctions";
import {
  useTable,
  usePagination,
  useFilters,
  useSortBy,
  useExpanded,
  useResizeColumns,
  useAbsoluteLayout,
  useBlockLayout,
  useFlexLayout,
} from "react-table";
import SortArrow from "../../Utility/SortArrow";
import TablePagination from "../../TablePagination";
import {
  deriveCODTypeFromDeliveryInfo,
  deriveDeliveryTypeFromRefNo,
} from "../../../helpers/utilities";
import { viewStates } from "../../../constants";
import { PageLoadError } from "../../Utility/PageLoadError";
import { add as dateAdd, format as dateFormat, parseISO } from "date-fns";

export const NeverScannedResultsViewV2Table = ({
  neverScannedResults,
  viewState,
}) => {
  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);
  const filterTypes = useMemo(
    () => ({
      ...getCommonFilterTypes(),
    }),
    []
  );
  const alphaNumericCaseInsensitiveSortFunction = useMemo(() => {
    return alphaNumericCaseInsensitive;
  }, []);
  const tableData = useMemo(() => {
    return neverScannedResults.map((barcodeObject) => {
      return {
        orderCreated: barcodeObject.deliveryInfo
          ? dateFormat(
              parseISO(barcodeObject.deliveryInfo.entered),
              "yyyy-MM-dd"
            )
          : "Not Found",

        deliveryDate: barcodeObject.deliveryInfo
          ? dateFormat(parseISO(barcodeObject.deliveryInfo.tdate), "yyyy-MM-dd")
          : "Not Found",
        invoice: barcodeObject.deliveryInfo?.invoice,
        barcode: barcodeObject.barcode,
        customer: {
          name: barcodeObject.deliveryInfo?.customer,
          address: barcodeObject.deliveryInfo?.address,
          city: barcodeObject.deliveryInfo?.city,
          state: barcodeObject.deliveryInfo?.st,
          zip: barcodeObject.deliveryInfo?.zip,
          phone: barcodeObject.deliveryInfo?.phone,
        },
        route: barcodeObject.deliveryInfo?.route,
        COD: deriveCODTypeFromDeliveryInfo(barcodeObject.deliveryInfo),
        deliveryType: deriveDeliveryTypeFromRefNo(
          barcodeObject.deliveryInfo?.refno
        ),
      };
    });
  }, [neverScannedResults]);

  const tableColumns = useMemo(
    () => [
      {
        Header: "Order Created",
        accessor: "orderCreated",
        width: 90,
        // filter: "includesTrimmed",
        disableFilters: true,
      },
      {
        Header: "Expected Delivery Date",
        accessor: "deliveryDate",
        width: 110,
        // filter: "includesTrimmed",
        disableFilters: true,
      },
      {
        Header: "Invoice",
        accessor: "invoice",
        width: 90,
        filter: "includesTrimmed",
        sortType: alphaNumericCaseInsensitiveSortFunction,
      },
      {
        Header: "Barcode",
        accessor: "barcode",
        width: 90,
        filter: "includesTrimmed",
      },
      {
        Header: "Customer",
        accessor: (row) => {
          const customerObject = row.customer;
          return `${customerObject.name} ${customerObject.address} ${customerObject.city}, ${customerObject.state} ${customerObject.zip}`;
        },
        Cell: (cellData) => {
          const customerObject = cellData.row.original.customer;
          return (
            <span>
              {customerObject.name} <br />
              {customerObject.address} | {customerObject.city},{" "}
              {customerObject.state} {customerObject.zip}
            </span>
          );
        },
        width: 200,
        filter: "includesTrimmed",
        sortType: alphaNumericCaseInsensitiveSortFunction,
      },
      {
        Header: "Route",
        accessor: "route",
        width: 100,
        filter: "includesTrimmed",
        sortType: alphaNumericCaseInsensitiveSortFunction,
      },
      {
        Header: "COD",
        accessor: "COD",
        width: 90,
        filter: "includesTrimmed",
        disableSortBy: true,
        Filter: SelectColumnFilter,
      },
      {
        Header: "Delivery Type",
        accessor: "deliveryType",
        Filter: SelectColumnFilter,
        disableSortBy: true,
        width: 90,
      },
    ],
    [neverScannedResults]
  );

  // const initialState = { hiddenColumns: ["dtype"] }; // if you want to hide columns, give them an id in tableColumns and add the id to this array. Then pass this into useTable({otherParams, initialState}): https://stackoverflow.com/a/69477712/12156650

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    pageCount,
    previousPage,
    setPageSize,
    visibleColumns,
    state: { pageIndex, pageSize },
    toggleSortBy,
    sortedIndex,
    toggleAllRowsExpanded,
  } = useTable(
    {
      columns: tableColumns,
      data: tableData,
      defaultColumn,
      filterTypes,
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useFlexLayout,
    useResizeColumns
  );

  useEffect(() => {
    toggleSortBy("orderCreated", true, false);
  }, [toggleSortBy, tableData]);

  return (
    <>
      <div className="table-response order-history-table-container">
        <table className="table col-12" {...getTableProps()}>
          <thead className="order-history-table-header">
            {headerGroups.map((headerGroup) => {
              return (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => {
                    const columnHeaderProps = column.getHeaderProps(
                      column.getSortByToggleProps()
                    );

                    return (
                      <th
                        className="order-history-th"
                        {...columnHeaderProps}
                        onClick={(e) => {
                          if (
                            !columnHeaderProps.onClick || //if the column cannot sort, do nothing on click. If you try to call columnHeaderProps.onClick on a column without sorting or some other onClick set in react table it seems like it will error out.
                            e.target.tagName.toLowerCase() === "input" || //we do not want to toggle the sorting if user tries to click into input or select
                            e.target.tagName.toLowerCase() === "select"
                          ) {
                            return;
                          }
                          columnHeaderProps.onClick(e);
                        }}
                      >
                        {column.render("Header")}
                        {column.canSort && (
                          <SortArrow
                            isSorted={column.isSorted}
                            isSortedDesc={column.isSortedDesc}
                          />
                        )}
                        {column.canFilter && column.render("Filter")}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {viewState === viewStates.initial && (
              <tr
                style={{
                  padding: "100px",
                  height: "100%",
                  width: "100%",
                  display: "grid",
                  placeContent: "center",
                  textAlign: "center",
                }}
              >
                <td style={{ border: "none" }}>
                  <div>
                    <div>Select a date range and submit to view results.</div>
                  </div>
                </td>
              </tr>
            )}
            {viewState === viewStates.loading && (
              <tr
                style={{
                  padding: "100px",
                  height: "100%",
                  width: "100%",
                  display: "grid",
                  placeContent: "center",
                  textAlign: "center",
                }}
              >
                <td style={{ border: "none" }}>
                  <div>
                    <div className="spinner-border p-3 mb-1" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <div>Loading Results</div>
                  </div>
                </td>
              </tr>
            )}
            {viewState === viewStates.error && (
              <tr
                style={{
                  height: "100%",
                  width: "100%",
                  display: "grid",
                  placeContent: "center",
                }}
              >
                <td style={{ border: "none" }}>
                  <PageLoadError
                    showRefreshButton={false}
                    containerStyle={{ textAlign: "center", padding: "100px" }}
                    errorMessage="There was an issue while loading table data, please try again."
                  />
                </td>
              </tr>
            )}
            {viewState === viewStates.success &&
              (page.length > 0 ? (
                page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <td {...cell.getCellProps()} className="multi-line">
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr
                  style={{
                    padding: "100px",
                    height: "100%",
                    width: "100%",
                    display: "grid",
                    placeContent: "center",
                    textAlign: "center",
                  }}
                >
                  <td style={{ border: "none" }}>
                    <div>
                      <div>No Results Found</div>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="col-12">
        <TablePagination
          onPreviousPageClick={() => {
            previousPage();
          }}
          onNextPageClick={() => {
            nextPage();
          }}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          gotoPage={(val) => {
            gotoPage(val);
          }}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          pageSize={pageSize}
          pageCount={pageCount}
          setPageSize={(e) => setPageSize(Number(e.target.value))}
        />
      </div>
    </>
  );
};
